import React from "react";

import { FarmerType, useTranslation } from "utils";

import { Flex, Icon, LazyLoadImage, Link, Svg, Typography } from "components";

interface BulletPointsProps {
  items: string[] | undefined;
}

interface IntroScreenProps {
  farmerType: FarmerType;
}

interface IntroScreenNativeMobileProps {
  className?: string;
  farmerType: FarmerType;
}

const BulletPoints = ({ items }: BulletPointsProps) => {
  if (!Array.isArray(items)) return null;
  return (
    <Flex vertical gap={24}>
      {items.map((item, index) => (
        <Flex align="center" gap={8} data-testid={`intro-screen-checkpoint-${index}`} className="intro-screen-list-items" key={index}>
          <Icon className="intro-screen-list-items-icon" size="m" iconName="tabler/circle-check" />
          <Typography variant="h4">{item}</Typography>
        </Flex>
      ))}
    </Flex>
  );
};

const BulletPointsMobile = ({ items }: BulletPointsProps) => {
  if (!Array.isArray(items)) return null;
  return (
    <Flex vertical gap={16}>
      {items.map((item, index) => (
        <Flex align="center" gap={8} data-testid={`intro-screen-checkpoint-${index}`} className="intro-screen-native-mobile-list-items" key={index}>
          <Icon className="intro-screen-native-mobile-list-items-icon" size="s" iconName="tabler/circle-check" />
          <Typography variant="paragraph-m">{item}</Typography>
        </Flex>
      ))}
    </Flex>
  );
};

const getIntroImage = (farmerType: FarmerType): string => {
  const imageMap: Record<FarmerType, string> = {
    // TODO: Replace with dairy and meat related images that aren't AI generated
    dairy: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro.jpg`,
    meat: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro.jpg`,
    default: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro.jpg`,
  };

  return imageMap[farmerType];
};

const getIntroImageMobile = (farmerType: FarmerType): string => {
  const imageMap: Record<FarmerType, string> = {
    // TODO: Replace with dairy and meat related images that aren't AI generated
    dairy: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro_mobile.png`,
    meat: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro_mobile.png`,
    default: `${process.env.REACT_APP_FARM_URL}/images/intro/split_screen_intro_mobile.png`,
  };

  return imageMap[farmerType];
};

export const IntroScreen = ({ farmerType }: IntroScreenProps) => {
  const { t } = useTranslation("intro");

  const getHeadline = () => t(`intro.headline.${farmerType}`);

  const getMessages = () => t(`intro.messages.${farmerType}`, { returnObjects: true }) as string[];

  return (
    <div className="intro-screen">
      <LazyLoadImage alt="" src={getIntroImage(farmerType)} />
      <Flex vertical gap={32} className="container">
        <Link to="/" mixpanelText="klim-logo">
          <Svg svgName="logos/klim_logo_text_black" width={75} height={32} />
        </Link>
        <Typography variant="h1">{getHeadline()}</Typography>
        <BulletPoints items={getMessages()} />
      </Flex>
    </div>
  );
};

export const IntroScreenNativeMobile = ({ className, farmerType }: IntroScreenNativeMobileProps) => {
  const { t } = useTranslation("intro");

  const getHeadline = () => {
    const key = farmerType === "dairy" ? "intro.headline.dairy" : "intro.headline";
    return t(key);
  };

  const getMessages = () => {
    const key = farmerType === "dairy" ? "intro.messages.dairy" : "intro.messages";
    return t(key, { returnObjects: true }) as string[];
  };

  return (
    <div className={`intro-screen-native-mobile ${className || ""}`}>
      <div className="intro-screen-native-mobile-container">
        <LazyLoadImage alt="" src={getIntroImageMobile(farmerType)} />
        <Flex vertical gap={32} className="content">
          <Typography variant="h1">{getHeadline()}</Typography>
          <BulletPointsMobile items={getMessages()} />
        </Flex>
      </div>
    </div>
  );
};
